<script setup lang="ts">
import { computed } from "vue";
import { routeForInstall } from "@os1/core/device";
import { useDevice } from "../composables/useDevice";
import { canonicalUrl, mobileUrl } from "../config";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: "close"): void }>();

const device = useDevice();

type InstallCopy = {
  title: string;
  lead: string;
  primary: string;
  secondary: string;
  note: string;
};

const copy = computed<InstallCopy>(() => {
  switch (device.installTarget) {
    case "mobile-apk":
      return {
        title: "Take OS1 with you",
        lead:
          "OS1 Mobile is a small companion for browsing the catalog, saved looks, and the local-pairing handshake with the desktop app.",
        primary: "Download the APK",
        secondary: "Open the responsive site",
        note: "APK signed by OS1. Install requires \"Install from unknown sources\" enabled."
      };
    case "pwa-add-to-home":
      return device.os === "ios"
        ? {
            title: "Add OS1 to your Home Screen",
            lead:
              "iOS doesn't take APKs. Add OS1 to your Home Screen and it behaves like a native app — full screen, saved looks, offline catalog.",
            primary: "Show me how (iOS)",
            secondary: "Continue in browser",
            note: "Tap the Share button, then \"Add to Home Screen\". No App Store account required."
          }
        : {
            title: "Add OS1 to your Home Screen",
            lead:
              "OS1 works as an installable PWA. Add it to your Home Screen for the full-screen, saved-looks, offline-catalog experience.",
            primary: "Install OS1",
            secondary: "Continue in browser",
            note: "Local-first. Catalog cached on device. No telemetry."
          };
    case "desktop-app":
    default: {
      if (device.os === "macos") {
        return {
          title: "Install OS1 for macOS",
          lead:
            "OS1 Desktop is a quiet companion: local search, dedupe, large-file map, cloud coverage. Universal binary, notarized.",
          primary: "Download OS1.dmg",
          secondary: "View release notes",
          note: "Local-first. No telemetry. Apple silicon and Intel."
        };
      }
      if (device.os === "linux") {
        return {
          title: "Install OS1 for Linux",
          lead:
            "AppImage and .deb available. Talks to Everything via the HTTP bridge on a Windows machine, or to your local index when configured.",
          primary: "Download OS1.AppImage",
          secondary: "View release notes",
          note: "Local-first. No telemetry. x86_64."
        };
      }
      return {
        title: "Install OS1 for Windows",
        lead:
          "OS1 Desktop is the operator's app. It installs alongside Everything, indexes the disk, finds duplicates and large files, logs every scan so the next version can beat the last.",
        primary: "Download OS1 Setup.exe",
        secondary: "View release notes",
        note: "Requires Everything 1.5a. We'll install it for you if it isn't there. Local-first. No telemetry."
      };
    }
  }
});

const primaryHref = computed(() => {
  const route = routeForInstall(device.installTarget);
  return device.installTarget === "desktop-app" ? canonicalUrl(route) : mobileUrl(route);
});

const onBackdrop = (event: MouseEvent) => {
  if ((event.target as HTMLElement).classList.contains("install__backdrop")) {
    emit("close");
  }
};
</script>

<template>
  <Transition name="install">
    <div v-if="props.open" class="install__backdrop" @click="onBackdrop">
      <div class="install" role="dialog" aria-modal="true">
        <button class="install__close" @click="emit('close')" aria-label="Dismiss">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none">
            <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>

        <div class="install__visual" aria-hidden="true">
          <svg viewBox="0 0 220 180" class="install__svg">
            <defs>
              <linearGradient id="install-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#ff4632" />
                <stop offset="100%" stop-color="#4a0d0d" />
              </linearGradient>
            </defs>
            <rect width="220" height="180" fill="url(#install-grad)" />
            <g stroke="#ffd4c4" stroke-width="0.6" fill="none" opacity="0.45">
              <path d="M0 90 Q 55 60 110 90 T 220 90" />
              <path d="M0 100 Q 55 130 110 100 T 220 100" />
              <path d="M0 80 Q 55 50 110 80 T 220 80" />
            </g>
            <circle cx="110" cy="90" r="34" fill="rgba(255,255,255,0.06)" stroke="rgba(255,212,196,0.5)" />
            <circle cx="110" cy="90" r="6" fill="#ffd4c4" />
          </svg>
        </div>

        <div class="install__body">
          <span class="os1-eyebrow">Companion app</span>
          <h3 class="install__title">{{ copy.title }}</h3>
          <p class="install__lead">{{ copy.lead }}</p>

          <ul class="install__list">
            <li><span class="install__dot" /> Local-first index</li>
            <li><span class="install__dot" /> Duplicate map and large-file scan</li>
            <li><span class="install__dot" /> Backup / cloud coverage heuristics</li>
            <li><span class="install__dot" /> Reproducible benchmark logs</li>
          </ul>

          <div class="install__actions">
            <a class="os1-button os1-button--solid" :href="primaryHref" rel="noopener">
              {{ copy.primary }}
            </a>
            <button class="os1-button os1-button--ghost" @click="emit('close')">
              {{ copy.secondary }}
            </button>
          </div>

          <p class="install__note">{{ copy.note }}</p>

          <div class="install__meta">
            <span>Detected: <strong>{{ device.class }}</strong></span>
            <span>·</span>
            <span>{{ device.os }}</span>
            <span>·</span>
            <span>{{ device.installTarget }}</span>
            <span v-if="device.isStandalone">·</span>
            <span v-if="device.isStandalone">standalone</span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.install__backdrop {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(6, 1, 1, 0.78);
  backdrop-filter: blur(10px);
  display: grid;
  place-items: end center;
  padding: clamp(0.6rem, 3vw, 2rem);
}

.install {
  position: relative;
  display: grid;
  grid-template-columns: 220px 1fr;
  width: min(100%, 720px);
  background: linear-gradient(180deg, #1c0808 0%, #150505 100%);
  border: 1px solid var(--os1-line-strong);
  border-radius: var(--os1-radius);
  overflow: hidden;
  box-shadow: var(--os1-shadow);
  margin-bottom: clamp(0.5rem, 4vw, 2rem);
  align-self: end;
}

.install__close {
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  z-index: 4;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: rgba(8, 2, 2, 0.7);
  border: 1px solid var(--os1-line-strong);
  color: var(--os1-ink-soft);
}

.install__close:hover {
  color: var(--os1-rose);
}

.install__visual {
  background: #1a0606;
}

.install__svg {
  width: 100%;
  height: 100%;
  display: block;
}

.install__body {
  padding: clamp(1.2rem, 3vw, 1.8rem);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.install__title {
  font-size: var(--os1-step-3);
  color: var(--os1-paper);
  line-height: 1;
}

.install__lead {
  color: var(--os1-ink-soft);
  font-size: 0.88rem;
  max-width: 46ch;
}

.install__list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.5rem;
  color: var(--os1-ink-soft);
  font-size: 0.82rem;
}

.install__list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.install__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--os1-red);
}

.install__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.7rem;
  flex-wrap: wrap;
}

.install__note {
  color: var(--os1-ink-mute);
  font-size: 0.72rem;
  margin-top: 0.5rem;
}

.install__meta {
  font-family: var(--os1-font-mono);
  font-size: 0.62rem;
  color: var(--os1-ink-mute);
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.4rem;
  text-transform: lowercase;
}

@media (max-width: 720px) {
  .install {
    grid-template-columns: 1fr;
  }
  .install__visual {
    height: 120px;
  }
}

.install-enter-active,
.install-leave-active {
  transition: opacity 240ms ease;
}
.install-enter-active .install,
.install-leave-active .install {
  transition: transform 280ms ease, opacity 240ms ease;
}
.install-enter-from,
.install-leave-to {
  opacity: 0;
}
.install-enter-from .install,
.install-leave-to .install {
  transform: translateY(20px);
  opacity: 0;
}
</style>
